import { Empty, Pagination, Spin, Table as AntTable } from 'antd';
import React, { useState } from 'react';

import { RoleR } from '../../../services/roles';
import { AddOrEditModal } from './AddOrEditModal';
import { TableActions } from './TableAction';

type TableProps = {
  roles: RoleR[];
  loadingTable: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onEdit: () => void;
  onPaginationChange: (value: number) => void;
};

export const Table = ({
  roles,
  loadingTable,
  totalItems,
  currentPage,
  pageSize,
  onEdit,
  onPaginationChange
}: TableProps): JSX.Element => {
  const [currEditIndex, setCurrEditIndex] = useState(-1);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const closeEditModal = () => {
    setCurrEditIndex(-1);
    setEditModalVisible(false);
  };

  const openEditModal = (index: number) => {
    setCurrEditIndex(index);
    setEditModalVisible(true);
  };

  const columns = [
    {
      title: 'ID #',
      dataIndex: 'id'
    },
    {
      title: 'Name',
      dataIndex: 'name'
    },
    {
      title: 'Label',
      dataIndex: 'label'
    },
    {
      title: 'Actions',
      width: 150,
      key: 'operation',
      align: 'center' as const,
      render: (_: any, __: RoleR, index: number) => {
        return <TableActions onEditClick={() => openEditModal(index)} />;
      }
    }
  ];

  const pagination = (
    <Pagination
      className="ant-table-pagination ant-table-pagination-right"
      total={totalItems}
      current={currentPage}
      onChange={onPaginationChange}
      showSizeChanger={false}
      pageSize={pageSize}
    />
  );

  return (
    <div>
      <div className="hidden md:block">
        <AntTable
          style={{ marginTop: '3%' }}
          loading={loadingTable}
          rowKey={({ id }) => id}
          dataSource={roles}
          columns={columns}
          pagination={false}
          scroll={{ x: 1200, scrollToFirstRowOnChange: true }}
          expandRowByClick={true}
          onRow={(_, index) => ({
            onClick: () => openEditModal(index!)
          })}
          footer={() => pagination}
        />
      </div>

      <div className="block md:hidden">
        {loadingTable ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : roles.length === 0 ? (
          <div className="rounded-lg border border-grey-border bg-surface py-10">
            <Empty />
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {roles.map((role, index) => (
              <li
                key={role.id}
                role="button"
                tabIndex={0}
                onClick={() => openEditModal(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openEditModal(index);
                  }
                }}
                className="cursor-pointer rounded-lg border border-grey-border bg-surface p-4 shadow-sm transition-colors active:bg-grey-bg"
              >
                <div className="min-w-0">
                  <div className="truncate text-base font-semibold text-grey-text">{role.name}</div>
                  <div className="mt-0.5 text-xs text-grey-secondary">ID #{role.id}</div>
                </div>

                <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
                  <dt className="text-grey-secondary">Label</dt>
                  <dd className="truncate text-grey-text">{role.label}</dd>
                </dl>

                <div className="mt-3 flex justify-end border-t border-grey-border pt-3">
                  <TableActions onEditClick={() => openEditModal(index)} />
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex justify-end">{pagination}</div>
      </div>

      {currEditIndex !== -1 && (
        <AddOrEditModal
          modalVisible={editModalVisible}
          role={roles[currEditIndex]}
          onCancel={closeEditModal}
          onSuccess={() => {
            closeEditModal();
            onEdit();
          }}
        />
      )}
    </div>
  );
};
